// =====================================================================
// GRID GUARD — bulletproof Child Table fix (inline-style enforcement)
// =====================================================================
// Root cause history: Frappe v16 opens the child-table row editor as
// `.grid-row-open .form-in-grid { position:fixed }` and dims the page
// with `#freeze.grid-form { z-index:1020 }` (editor must be 1021+).
// ANY stylesheet (theme OR another app OR future change) that adds a
// transform/filter to a grid ancestor, or raises the freeze z-index,
// re-hides the editor → "blank + hang".
//
// THE GENIUS, FINAL FIX: don't fight CSS. Hook the exact Frappe call
// (`frappe.dom.freeze` with the `grid-form` class) and, the moment the
// editor opens, set INLINE styles on it + the freeze. Inline styles set
// with priority "important" beat EVERY stylesheet rule in existence —
// so the editor is GUARANTEED fixed, centred and above the freeze, no
// matter what any CSS does, now or ever. Pure Frappe mechanism, just
// outcome-guaranteed. Reverted automatically on close (Frappe removes
// the nodes). Zero observers, zero layout reads — one rAF on open.
// =====================================================================

const Z_EDITOR = "2147483000";
const Z_FREEZE = "2147482000";

// Strip Frappe's JS-set inline max-height / fixed height on the grid
// body so it never shows an inner vertical scroll. Frappe sometimes
// sets these via JavaScript on .grid-body / .form-grid-container which
// only inline-style overrides can defeat.
function stripGridInlineHeight() {
	try {
		if (!document.querySelector(".form-grid, .form-grid-container")) return;
		const targets = document.querySelectorAll(
			".form-grid, .form-grid-container, .form-grid .grid-body, .form-grid .rows, .grid-field, .frappe-control[data-fieldtype='Table']"
		);
		targets.forEach((el) => {
			if (el.style) {
				if (el.style.maxHeight && el.style.maxHeight !== "none") {
					el.style.setProperty("max-height", "none", "important");
				}
				if (el.style.height && el.style.height !== "auto") {
					el.style.setProperty("height", "auto", "important");
				}
				if (el.style.overflowY && el.style.overflowY !== "visible") {
					el.style.setProperty("overflow-y", "visible", "important");
				}
			}
		});
	} catch (e) {}
}

function enforce() {
	stripGridInlineHeight();
	try {
		const fz =
			document.querySelector("#freeze.grid-form") ||
			document.querySelector("#freeze");
		if (fz) {
			fz.style.setProperty("z-index", Z_FREEZE, "important");
			fz.style.setProperty("backdrop-filter", "none", "important");
			fz.style.setProperty("-webkit-backdrop-filter", "none", "important");
		}
		const fig = document.querySelector(".grid-row-open .form-in-grid");
		if (fig) {
			fig.style.setProperty("position", "fixed", "important");
			fig.style.setProperty("z-index", Z_EDITOR, "important");
			fig.style.setProperty("opacity", "1", "important");
			fig.style.setProperty("visibility", "visible", "important");
			// BULLETPROOF centring — works identically in LTR & RTL with
			// NO transform (transform + left:50% mis-resolved in RTL and
			// threw the editor off-screen). left:0 + right:0 + margin auto
			// + fixed width = perfectly centred in the viewport, always.
			fig.style.setProperty("transform", "none", "important");
			fig.style.setProperty("left", "0", "important");
			fig.style.setProperty("right", "0", "important");
			fig.style.setProperty("margin-left", "auto", "important");
			fig.style.setProperty("margin-right", "auto", "important");
			fig.style.setProperty("width", "min(960px, 94vw)", "important");
			fig.style.setProperty("max-width", "96vw", "important");
			fig.style.setProperty("top", "5vh", "important");
			fig.style.setProperty("max-height", "90vh", "important");
			fig.style.setProperty("overflow", "auto", "important");
		}
	} catch (e) {}
}

function placeOpenGridDropdowns() {
	try {
		document
			.querySelectorAll(".grid-row-open .form-in-grid .awesomplete > ul:not([hidden])")
			.forEach((ul) => {
				const wrap = ul.closest(".awesomplete");
				const input = wrap && wrap.querySelector("input");
				if (!input) return;
				const rect = input.getBoundingClientRect();
				const spaceBelow = window.innerHeight - rect.bottom - 12;
				const maxHeight = Math.max(160, Math.min(320, spaceBelow));
				const isRtl = document.documentElement.dir === "rtl";
				const dropdownWidth = Math.min(
					Math.max(rect.width, 280),
					window.innerWidth - 16
				);
				const rawLeft = isRtl ? rect.right - dropdownWidth : rect.left;
				const left = Math.min(
					Math.max(8, rawLeft),
					Math.max(8, window.innerWidth - dropdownWidth - 8)
				);
				ul.style.setProperty("position", "fixed", "important");
				ul.style.setProperty("left", `${left}px`, "important");
				ul.style.setProperty("right", "auto", "important");
				ul.style.setProperty("top", `${rect.bottom + 4}px`, "important");
				ul.style.setProperty("width", `${dropdownWidth}px`, "important");
				ul.style.setProperty("max-width", "min(520px, calc(100vw - 16px))", "important");
				ul.style.setProperty("max-height", `${maxHeight}px`, "important");
				ul.style.setProperty("overflow-y", "auto", "important");
				ul.style.setProperty("z-index", "2147483200", "important");
			});
	} catch (e) {}
}

let dropdownRaf = 0;
let dropdownTimer = 0;
function hasOpenGridDropdown() {
	return !!document.querySelector(".grid-row-open .form-in-grid .awesomplete > ul:not([hidden])");
}

function scheduleDropdownPlacement() {
	if (!hasOpenGridDropdown()) return;
	if (!dropdownRaf) {
		dropdownRaf = requestAnimationFrame(() => {
			dropdownRaf = 0;
			placeOpenGridDropdowns();
		});
	}
	clearTimeout(dropdownTimer);
	dropdownTimer = setTimeout(placeOpenGridDropdowns, 80);
}

let patched = false;
function patch() {
	if (patched) return;
	if (!(window.frappe && frappe.dom && typeof frappe.dom.freeze === "function"))
		return;
	if (frappe.dom.__e16_grid_guard) return;

	const origFreeze = frappe.dom.freeze;
	frappe.dom.freeze = function (msg, cssClass) {
		const out = origFreeze.apply(this, arguments);
		try {
			if (
				typeof cssClass === "string" &&
				cssClass.indexOf("grid-form") !== -1
			) {
				// run after Frappe finishes inserting the editor
				requestAnimationFrame(enforce);
				setTimeout(enforce, 80);
				setTimeout(enforce, 250);
			}
		} catch (e) {}
		return out;
	};

	frappe.dom.__e16_grid_guard = true;
	patched = true;
}

export const gridGuard = {
	mount() {
		if (window.frappe && frappe.dom && frappe.dom.freeze) {
			patch();
		} else {
			let n = 0;
			const iv = setInterval(() => {
				n++;
				if (window.frappe && frappe.dom && frappe.dom.freeze) {
					patch();
					clearInterval(iv);
				} else if (n > 80) {
					clearInterval(iv);
				}
			}, 250);
		}

		// Strip JS-injected max-height/height from grid containers on
		// every form route — this kills the inner vertical scroll the
		// user kept reporting (sticky headers fading in/out as the grid
		// body scrolls internally).
		const stripPeriodically = () => {
			try { stripGridInlineHeight(); } catch (e) {}
		};
		const startStripper = () => {
			stripPeriodically();
			setTimeout(stripPeriodically, 400);
			setTimeout(stripPeriodically, 1200);
			setTimeout(stripPeriodically, 2500);
		};
		if (document.readyState === "loading") {
			document.addEventListener("DOMContentLoaded", startStripper, { once: true });
		} else {
			startStripper();
		}
		try {
			if (window.frappe && frappe.router && frappe.router.on) {
				frappe.router.on("change", () => setTimeout(startStripper, 200));
			}
		} catch (e) {}

		document.addEventListener(
			"awesomplete-open",
			(e) => {
				if (e.target && e.target.closest && e.target.closest(".grid-row-open .form-in-grid")) {
					scheduleDropdownPlacement();
				}
			},
			true
		);
		document.addEventListener(
			"input",
			(e) => {
				if (e.target && e.target.closest && e.target.closest(".grid-row-open .form-in-grid .awesomplete")) {
					scheduleDropdownPlacement();
				}
			},
			true
		);
		document.addEventListener("scroll", scheduleDropdownPlacement, true);
		window.addEventListener("resize", scheduleDropdownPlacement, { passive: true });
	},
};
